import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { CircularProgress } from '@mui/material'
import { getDocumentSuggestions } from 'services/reportingService'
import { useSelector } from 'react-redux'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'

const DocumentSuggestionPanel = ({ reportType }) => {
  const bp = useSelector(getSelectedBp)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (!bp?.id || !reportType) return
    setLoading(true)
    getDocumentSuggestions(bp.id, reportType)
      .then(res => setData(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [bp?.id, reportType])

  if (loading) return <LoadingWrap><CircularProgress size={18} sx={{ color: '#D24724' }} /></LoadingWrap>
  if (!data || !data.sections?.length) return null

  const { summary, sections } = data
  const pct = Math.round((summary.completeSections / Math.max(summary.totalSections, 1)) * 100)
  const hasGaps = summary.sectionsNeedingDocuments > 0

  return (
    <Panel>
      <PanelHeader onClick={() => setExpanded(!expanded)}>
        <HeaderInfo>
          <StatusDot $pct={pct} />
          <HeaderTitle>Document Readiness</HeaderTitle>
          <Pct $pct={pct}>{pct}%</Pct>
          <HeaderMeta>{summary.completeSections}/{summary.totalSections} sections covered</HeaderMeta>
        </HeaderInfo>
        <Toggle>{expanded ? '−' : '+'}</Toggle>
      </PanelHeader>

      {expanded && (
        <SectionList>
          {sections.map((section, i) => (
            <Row key={i}>
              <RowDot $status={section.status} />
              <RowBody>
                <RowTitle>{section.title}</RowTitle>
                {section.found?.length > 0 && (
                  <Tags>
                    {section.found.map((d, j) => <Tag key={j} $ok>{d}</Tag>)}
                  </Tags>
                )}
                {section.missing?.length > 0 && (
                  <Tags>
                    {section.missing.map((d, j) => <Tag key={j} $warn>Missing: {d}</Tag>)}
                  </Tags>
                )}
              </RowBody>
            </Row>
          ))}
        </SectionList>
      )}
    </Panel>
  )
}

export default DocumentSuggestionPanel

const LoadingWrap = styled.div` padding: 10px; display: flex; justify-content: center; `

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 8px;
  margin: 0 20px 16px;
  overflow: hidden;
`

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.grey?.[400] || '#fafafa'}; }
`

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $pct }) => $pct >= 80 ? '#059669' : $pct >= 40 ? '#f59e0b' : '#dc2626'};
  flex-shrink: 0;
`

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const Pct = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $pct }) => $pct >= 80 ? '#059669' : $pct >= 40 ? '#f59e0b' : '#dc2626'};
`

const HeaderMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const Toggle = styled.span`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  line-height: 1;
  user-select: none;
`

const SectionList = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
`

const Row = styled.div`
  display: flex;
  gap: 10px;
  padding: 8px 14px;
  align-items: flex-start;
  &:not(:last-child) { border-bottom: 1px solid #f2f2f2; }
`

const RowDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
  background: ${({ $status }) =>
    $status === 'complete' ? '#059669' :
    $status === 'partial' ? '#f59e0b' : '#dc2626'};
`

const RowBody = styled.div` flex: 1; min-width: 0; `

const RowTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`

const Tag = styled.span`
  font-size: 10px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 3px;
  color: ${({ $ok, $warn }) => $ok ? '#059669' : $warn ? '#b91c1c' : '#666'};
  background: ${({ $ok, $warn }) => $ok ? '#ecfdf5' : $warn ? '#fef2f2' : '#f9fafb'};
`
